
import { computed, defineComponent, reactive, watch } from 'vue'

import ymaps from 'yandex-maps'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import NewRadio from '@/common/new-components/NewRadio/index.vue'
import NewMap from '@/common/new-components/NewMap/index.vue'
import NewList from '@/common/new-components/NewList/index.vue'
import NewModal from '@/common/new-components/NewModal/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import TheCheckbox from '@/common/components/UI/Checkbox/index.vue'

// @module/components
import SectorListItem from '@/modules/NewDispatcher/components/SectorListItem/index.vue'
import DispatcherSectorListItem from '@/modules/NewDispatcher/components/DispatcherSectorListItem/index.vue'
import ExpressZoneListItem from '@/modules/NewDispatcher/components/ExpressZoneListItem/index.vue'
import TimeSlotZoneListItem from '@/modules/NewDispatcher/components/TimeSlotZoneListItem/index.vue'
import NewSectorForm from '@/modules/NewDispatcher/components/NewSectorForm/index.vue'
import NewDispatcherSectorForm from '@/modules/NewDispatcher/components/NewDispatcherSectorForm/index.vue'
import NewExtendedSectorForm from '@/modules/NewDispatcher/components/NewExtendedSectorForm/index.vue'
import NewExpressZoneForm from '@/modules/NewDispatcher/components/NewExpressZoneForm/index.vue'
import NewTimeSlotZoneForm from '@/modules/NewDispatcher/components/NewTimeSlotZoneForm/index.vue'

// @composable
import useNotifications from '@/common/composable/useNotifications'
import useMap from '@/common/new-composable/useMap'
import useMapGeoObjects from '@/common/new-composable/useMapGeoObjects'
import useMapPaint from '@/common/new-composable/useMapPaint'
import useAccount from '@/common/composable/useAccount'

// @types
import {
    DispatcherSector,
    DispatcherSectorFormPayload,
} from '@/new-services/types/dispatcher-sectors'

import { Sector, SectorPayload, ExpressZone, CreateExpressZonePayload, UpdateExpressZonePayload } from '@/new-services/types/sectors'
import { Courier } from '@/new-services/types/couriers'
import { TimeSlotZone, TimeSlotZonePayload } from '@/new-services/types/time-slot-zones'

// @new-services
import dispatcherSectorsAPI from '@/new-services/dispatcher-sectors.service'
import sectorsAPI from '@/new-services/sectors.service'
import couriersAPI from '@/new-services/couriers.service'
import timeSlotZonesAPI from '@/new-services/time-slot-zones.service'

// @store
import { useNewDispatcherStore } from '@/modules/NewDispatcher/stores/useNewDispatcherStore'

const MAP_LAYERS = [
    {
        id: 1,
        name: 'Yandex',
        key: 'yandex#map',
    },
    {
        id: 2,
        name: '2GIS',
        key: '2gis#map',
    },
]

const SECTORS_OPTIONS = [
    {
        id: 1,
        name: 'Сектора доставки',
        key: 'delivery-sectors',
    },
    {
        id: 2,
        name: 'Сектора диспетчера',
        key: 'dispatcher-sectors',
    },
    {
        id: 3,
        name: 'Экспресс',
        key: 'express'
    },
    {
        id: 4,
        name: 'Сектора тайм-слотов',
        key: 'time-slot-zones'
    }
]

export default defineComponent({
    components: {
        PageTitle,
        NewRadio,
        NewMap,
        NewList,
        SectorListItem,
        ExpressZoneListItem,
        TimeSlotZoneListItem,
        DispatcherSectorListItem,
        NewModal,
        NewSectorForm,
        NewDispatcherSectorForm,
        NewExtendedSectorForm,
        NewExpressZoneForm,
        NewTimeSlotZoneForm,
        NewButton,
        TheCheckbox,
    },
    setup() {
        const new_dispatcher_store = useNewDispatcherStore()

        const { havePermissions } = useAccount()

        const notifications = useNotifications()

        const {
            clearMap,
            addGeoObject,
            removeLastAddedGeoObject,
            setMapCenter,
            getLastAddedGeoObjectIndex,
        } = useMap()
        const map_graphics = useMapGeoObjects()
        const map_paint = useMapPaint()

        let map_instance = null as ymaps.Map | null

        const state = reactive({
            layer: 'yandex#map',
            sectors: { ...SECTORS_OPTIONS[0] },
            is_extended_sectors_showed: true,
        })

        // delivery sectors
        const list_state = reactive({
            is_loading: false,
            is_failed: false,
            options: [] as Sector[],
        })

        // dispatcher sectors
        const dispatcher_list_state = reactive({
            is_loading: false,
            is_failed: false,
            options: [] as DispatcherSector[],
        })

        const express_zones_state = reactive({
            is_loading: false,
            is_failed: false,
            options: [] as ExpressZone[]
        })

        const time_slot_zones_state = reactive({
            is_loading: false,
            is_failed: false,
            options: [] as TimeSlotZone[]
        })

        const delete_state = reactive({
            sector: null as DispatcherSector | Sector | ExpressZone | TimeSlotZone | null,
            is_express_zone: false,
            is_time_slot_zone: false,
            is_opened: false,
            is_loading: false,
        })

        const couriers_state = reactive({
            is_loading: false,
            is_failed: false,
            options: [] as Courier[]
        })

        // Only for delivery sectors
        const new_sector_state = reactive({
            is_opened: false,
            is_loading: false,
            is_create: false,
            is_update: false,
            //
            initial_value: null as Sector | null,
            sector_index: null as number | null,
            //
            name: null as string | null,
            coordinates: [] as number[][],
            color: null as string | null,
        })

        // Only for dispatcher sectors
        const new_dispatcher_sector_state = reactive({
            is_opened: false,
            is_loading: false,
            is_create: false,
            is_update: false,
            //
            initial_value: null as DispatcherSector | null,
            sector_index: null as number | null,
            //
            description: null as string | null,
            coordinates: [] as number[][],
        })

        const new_extended_sector_state = reactive({
            is_opened: false,
            is_create: false,
            is_update: false,
            is_loading: false,
            //
            dispatcherSector: null as DispatcherSector | null,
            sector_id: null as string | null,
            coordinates: [] as number[][],
        })

        const new_express_zone_state = reactive({
            is_opened: false,
            is_loading: false,
            is_create: false,
            is_update: false,
            //
            initial_value: null as ExpressZone | null,
            express_zone_index: null as number | null,
            //
            name: null as string | null,
            coordinates: [] as number[][],
            color: null as string | null,
        })

        const new_time_slot_zone_state = reactive({
            is_opened: false,
            is_loading: false,
            is_create: false,
            is_update: false,
            //
            initial_value: null as TimeSlotZone | null,
            time_slot_zone_index: null as number | null,
            //
            name: null as string | null,
            coordinates: [] as number[][],
            color: null as string | null,
        })

        const drawSector = (sector: Sector | DispatcherSector | ExpressZone | TimeSlotZone) => {
            let color = '#2B55FF'
            let hintContent = sector.name

            if ('color' in sector) {
                color = sector.color
            }

            if ('description' in sector) {
                hintContent = sector.description
            }

            if (typeof sector.coordinates === 'string') {
                const polygon = map_graphics.createPolygon({
                    coordinates: JSON.parse(sector.coordinates),
                    color,
                    hintContent,
                })

                addGeoObject(map_instance as ymaps.Map, polygon)
            } else {
                const polygon = map_graphics.createPolygon({
                    coordinates: sector.coordinates,
                    color,
                    hintContent,
                })

                addGeoObject(map_instance as ymaps.Map, polygon)
            }

            if (state.is_extended_sectors_showed) {
                if ('extendedCoordinates' in sector) {
                    if ((sector.extendedCoordinates as string | null) !== null) {
                        const polygon = map_graphics.createPolygon({
                            id: `extended-${sector.id}`,
                            coordinates: JSON.parse(sector.extendedCoordinates as string),
                            color: '#FF0000',
                            hintContent: `Внешний сектор ${sector.description}`,
                        })

                        addGeoObject(map_instance as ymaps.Map, polygon)
                    }
                }
            }
        }

        const drawSectors = (sectors: Sector[] | DispatcherSector[] | ExpressZone[] | TimeSlotZone[]) => {
            if (!map_instance) return

            sectors.forEach((sector: any) => {
                if (sector.coordinates !== 'null') drawSector(sector)
            })
        }

        const onExtendedSectorsChange = () => {
            onSectorsReload()
        }

        const loadDeliverySectors = async () => {
            if (!new_dispatcher_store.dispatcherSectorIds.length) {
                return
            }

            try {
                list_state.is_loading = true

                const response = await sectorsAPI.getSectors({
                    page: 1,
                    dispatcherSectorIds: new_dispatcher_store.dispatcherSectorIds,
                    limit: 9999,
                })
                list_state.options = response.data.data

                if (state.sectors.key === 'delivery-sectors') {
                    drawSectors(list_state.options)
                }

                list_state.is_failed = false
            } catch (error) {
                list_state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При загрузке секторов доставки произошла ошибка',
                })
            } finally {
                list_state.is_loading = false
            }
        }

        const loadExpressZones = async () => {
            try {
                express_zones_state.is_loading = true

                const response = await dispatcherSectorsAPI.getExpressZones({
                    page: 1,
                    limit: 9999,
                })

                express_zones_state.options = response.data.data

                if (state.sectors.key === 'express') {
                    drawSectors(express_zones_state.options)
                }

                express_zones_state.is_failed = false
            } catch (error) {
                express_zones_state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При загрузке экспресс зон произошла ошибка',
                })
            } finally {
                express_zones_state.is_loading = false
            }
        }

        const loadTimeSlotZones = async () => {
            try {
                time_slot_zones_state.is_loading = true

                const response = await timeSlotZonesAPI.getTimeSlotZones({
                    page: 1,
                    dispatcherSectorIds: new_dispatcher_store.dispatcherSectorIds,
                    limit: 9999,
                })

                time_slot_zones_state.options = response.data.data

                if (state.sectors.key === 'time-slot-zones') {
                    drawSectors(time_slot_zones_state.options)
                }

                time_slot_zones_state.is_failed = false
            } catch (error) {
                time_slot_zones_state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При загрузке секторов тайм-слотов произошла ошибка',
                })
            } finally {
                time_slot_zones_state.is_loading = false
            }
        }

        const loadDispatcherSectors = async () => {
            try {
                dispatcher_list_state.is_loading = true

                const response = await dispatcherSectorsAPI.getAllDispatcherSectors()
                dispatcher_list_state.options = response.data.data

                if (state.sectors.key === 'dispatcher-sectors') {
                    drawSectors(dispatcher_list_state.options)
                }

                dispatcher_list_state.is_failed = false
            } catch (error) {
                dispatcher_list_state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При загрузке секторов диспетчера произошла ошибка',
                })
            } finally {
                dispatcher_list_state.is_loading = false
            }
        }

        const loadCouriers = async () => {
            if (!new_dispatcher_store.dispatcherSectorIds.length) {
                return
            }

            try {
                couriers_state.is_loading = true

                const response = await couriersAPI.getCouriers({
                    page: 1,
                    dispatcherSectorIds: new_dispatcher_store.dispatcherSectorIds,
                    limit: 9999,
                })

                couriers_state.options = response.data.data

                couriers_state.is_failed = false
            } catch (error) {
                couriers_state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При загрузке списка курьеров произошла ошибка',
                })
            } finally {
                couriers_state.is_loading = false
            }
        }

        const onSectorsReload = () => {
            clearMap(map_instance as ymaps.Map)

            if (state.sectors.key === 'delivery-sectors') {
                loadDeliverySectors()
            } else if (state.sectors.key === 'dispatcher-sectors') {
                loadDispatcherSectors()
            } else if (state.sectors.key === 'express') {
                loadExpressZones()
            } else if (state.sectors.key === 'time-slot-zones') {
                loadTimeSlotZones()
            }
        }

        const onCancelDeleteSector = () => {
            delete_state.is_opened = false
            delete_state.is_express_zone = false
            delete_state.is_time_slot_zone = false
            delete_state.sector = null
        }

        const deleteSector = async (sector: Sector) => {
            try {
                delete_state.is_loading = true

                await sectorsAPI.deleteSector(sector.id)

                onSectorsReload()

                notifications.notify({ title: `Сектор ${sector.name} удален`, type: 'success' })
                onCancelDeleteSector()
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При удалении сектора произошла ошибка' })
            } finally {
                delete_state.is_loading = false
            }
        }

        const deleteDispatcherSector = async (dispatcher_sector: DispatcherSector) => {
            try {
                delete_state.is_loading = true

                await dispatcherSectorsAPI.deleteDispatcherSector(dispatcher_sector.id)

                onSectorsReload()

                notifications.notify({
                    title: `Сектор диспетчера ${dispatcher_sector.name} удален`,
                    type: 'success',
                })
                onCancelDeleteSector()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При удалении сектора диспетчера произошла ошибка',
                })
            } finally {
                delete_state.is_loading = false
            }
        }

        const deleteExpressZone = async (expressZone: ExpressZone) => {
            try {
                delete_state.is_loading = true

                await dispatcherSectorsAPI.deleteExpressZone(expressZone.id)

                onSectorsReload()

                notifications.notify({
                    title: `Экспресс зона ${expressZone.name} удалена`,
                    type: 'success',
                })
                onCancelDeleteSector()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При удалении экспресс зоны произошла ошибка',
                })
            } finally {
                delete_state.is_loading = false
            }
        }

        const deleteTimeSlotZone = async (timeSlotZone: TimeSlotZone) => {
            try {
                delete_state.is_loading = true

                await timeSlotZonesAPI.deleteTimeSlotZone(timeSlotZone.id)

                onSectorsReload()

                notifications.notify({
                    title: `Сектор тайм-слотов ${timeSlotZone.name} удален`,
                    type: 'success',
                })

                onCancelDeleteSector()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При удалении сектора тайм-слотов произошла ошибка',
                })
            } finally {
                delete_state.is_loading = false
            }
        }

        const deleteHandler = () => {
            if (state.sectors.key === 'delivery-sectors') {
                const sector = delete_state.sector as Sector
                deleteSector(sector)
            } else if (state.sectors.key === 'dispatcher-sectors') {
                const dispatcher_sector = delete_state.sector as DispatcherSector
                deleteDispatcherSector(dispatcher_sector)
            } else if (state.sectors.key === 'express') {
                const expressZone = delete_state.sector as ExpressZone
                deleteExpressZone(expressZone)
            } else if (state.sectors.key === 'time-slot-zones') {
                const timeSlotZone = delete_state.sector as TimeSlotZone
                deleteTimeSlotZone(timeSlotZone)
            }
        }

        const onDeleteSector = (sector: Sector | DispatcherSector) => {
            delete_state.sector = { ...sector }
            delete_state.is_opened = true
        }

        const onDeleteExpressZone = (expressZone: ExpressZone) => {
            delete_state.sector = { ...expressZone }
            delete_state.is_express_zone = true
            delete_state.is_opened = true
        }

        const onDeleteTimeSlotZone = (timeSlotZone: TimeSlotZone) => {
            delete_state.sector = { ...timeSlotZone }
            delete_state.is_time_slot_zone = true
            delete_state.is_opened = true
        }

        const setMapCenterBySelectedDispatcherSector = (dispatcher_sector: DispatcherSector) => {
            if (dispatcher_sector.city) {
                if (dispatcher_sector.city.latitude && dispatcher_sector.city.longitude) {
                    setMapCenter(map_instance as ymaps.Map, [
                        dispatcher_sector.city.latitude,
                        dispatcher_sector.city.longitude,
                    ])
                    return
                }
            }

            const centroid = map_graphics.getPolygonCentroid(
                JSON.parse(dispatcher_sector.coordinates)
            )
            setMapCenter(map_instance as ymaps.Map, centroid)
        }

        const onMapReady = (instance: ymaps.Map) => {
            map_instance = instance

            loadDeliverySectors()
            loadDispatcherSectors()
            loadCouriers()

            map_paint.addPaintModule()

            new_dispatcher_store.dispatcherSectors.forEach((sector) => {
                setMapCenterBySelectedDispatcherSector(sector)
            })
        }

        // New Dispatcher Sector Listener
        watch(
            () => new_dispatcher_store.dispatcherSectors,
            (new_value: DispatcherSector[]) => {
                new_value.forEach((sector) => {
                    setMapCenterBySelectedDispatcherSector(sector)
                })
            }
        )

        const resetNewSectorState = () => {
            new_sector_state.is_create = false
            new_sector_state.is_update = false
            new_sector_state.initial_value = null
            new_sector_state.sector_index = null

            new_sector_state.color = null
            new_sector_state.coordinates = []
            new_sector_state.name = null
        }

        const resetNewDispatcherSectorState = () => {
            new_dispatcher_sector_state.is_create = false
            new_dispatcher_sector_state.is_update = false
            new_dispatcher_sector_state.initial_value = null
            new_dispatcher_sector_state.sector_index = null

            new_dispatcher_sector_state.description = null
            new_dispatcher_sector_state.coordinates = []
        }

        const resetNewExpressZoneState = () => {
            new_express_zone_state.is_create = false
            new_express_zone_state.is_update = false
            new_express_zone_state.initial_value = null
            new_express_zone_state.express_zone_index = null

            new_express_zone_state.color = null
            new_express_zone_state.coordinates = []
            new_express_zone_state.name = null
        }

        const resetNewTimeSlotZoneState = () => {
            new_time_slot_zone_state.is_create = false
            new_time_slot_zone_state.is_update = false
            new_time_slot_zone_state.initial_value = null
            new_time_slot_zone_state.time_slot_zone_index = null

            new_time_slot_zone_state.color = null
            new_time_slot_zone_state.coordinates = []
            new_time_slot_zone_state.name = null
        }

        const closeNewSectorState = () => {
            if (map_paint.state.is_started) return

            resetNewSectorState()
            new_sector_state.is_opened = false
        }

        const closeNewDispatcherSectorState = () => {
            if (map_paint.state.is_started) return

            resetNewDispatcherSectorState()
            new_dispatcher_sector_state.is_opened = false
        }

        const closeNewExpressZoneState = () => {
            if (map_paint.state.is_started) return

            resetNewExpressZoneState()
            new_express_zone_state.is_opened = false
        }

        const closeNewTimeSlotZoneState = () => {
            if (map_paint.state.is_started) return

            resetNewTimeSlotZoneState()
            new_time_slot_zone_state.is_opened = false
        }

        const reset = () => {
            closeNewSectorState()
            closeNewDispatcherSectorState()
            closeNewExpressZoneState()
            closeNewTimeSlotZoneState()

            clearMap(map_instance as ymaps.Map)

            list_state.options = []
            dispatcher_list_state.options = []
            express_zones_state.options = []
            time_slot_zones_state.options = []
        }

        const reloadData = () => {
            if (!new_dispatcher_store.dispatcherSectorIds.length) {
                reset()
                return
            }

            clearMap(map_instance as ymaps.Map)
            loadCouriers()

            if (state.sectors.key === 'dispatcher-sectors') {
                drawSectors(dispatcher_list_state.options)
                return
            }

            if (state.sectors.key === 'delivery-sectors') {
                loadDeliverySectors()
                return
            }

            if (state.sectors.key === 'express') {
                loadExpressZones()
                return
            }

            if (state.sectors.key === 'time-slot-zones') {
                loadTimeSlotZones()
                return
            }
        }

        const onAreaPainted = ({
            coordinates,
            color,
        }: {
            coordinates: number[][]
            color: string
        }): void => {
            new_sector_state.coordinates = coordinates
            new_sector_state.color = color

            const polygon = map_graphics.createPolygon({
                coordinates,
                color,
                hintContent: new_sector_state.name,
            })

            addGeoObject(map_instance as ymaps.Map, polygon)

            const new_polygon_index = getLastAddedGeoObjectIndex(map_instance as ymaps.Map)
            map_graphics.startUpdatePolygonByIndex(map_instance as ymaps.Map, new_polygon_index)
        }

        const onDispatcherAreaPainted = ({
            coordinates,
            color,
        }: {
            coordinates: number[][]
            color: string
        }): void => {
            new_dispatcher_sector_state.coordinates = coordinates

            const polygon = map_graphics.createPolygon({
                coordinates,
                color,
                hintContent: new_dispatcher_sector_state.description,
            })

            addGeoObject(map_instance as ymaps.Map, polygon)

            const new_polygon_index = getLastAddedGeoObjectIndex(map_instance as ymaps.Map)
            map_graphics.startUpdatePolygonByIndex(map_instance as ymaps.Map, new_polygon_index)
        }

        const onExpressZoneAreaPainted = ({
            coordinates,
            color,
        }: {
            coordinates: number[][]
            color: string
        }): void => {
            new_express_zone_state.coordinates = coordinates
            new_express_zone_state.color = color

            const polygon = map_graphics.createPolygon({
                coordinates,
                color,
                hintContent: new_express_zone_state.name,
            })

            addGeoObject(map_instance as ymaps.Map, polygon)

            const new_polygon_index = getLastAddedGeoObjectIndex(map_instance as ymaps.Map)
            map_graphics.startUpdatePolygonByIndex(map_instance as ymaps.Map, new_polygon_index)
        }

        const onTimeSlotZoneAreaPainted = ({
            coordinates,
            color,
        }: {
            coordinates: number[][]
            color: string
        }): void => {
            new_time_slot_zone_state.coordinates = coordinates
            new_time_slot_zone_state.color = color

            const polygon = map_graphics.createPolygon({
                coordinates,
                color,
                hintContent: new_time_slot_zone_state.name,
            })

            addGeoObject(map_instance as ymaps.Map, polygon)

            const new_polygon_index = getLastAddedGeoObjectIndex(map_instance as ymaps.Map)
            map_graphics.startUpdatePolygonByIndex(map_instance as ymaps.Map, new_polygon_index)
        }

        const onStartPaint = ({ color, name }: { color: string; name: string }) => {
            new_sector_state.name = name

            map_paint.onStartPaint({
                map: map_instance as ymaps.Map,
                color,
                callback: onAreaPainted,
            })
        }

        const onDispatcherStartPaint = ({ description }: { description: string }) => {
            new_dispatcher_sector_state.description = description

            map_paint.onStartPaint({
                map: map_instance as ymaps.Map,
                color: '#2B55FF',
                callback: onDispatcherAreaPainted,
            })
        }

        const onExpressZoneStartPaint = ({ color, name }: { color: string; name: string }) => {
            new_express_zone_state.name = name

            map_paint.onStartPaint({
                map: map_instance as ymaps.Map,
                color,
                callback: onExpressZoneAreaPainted,
            })
        }

        const onTimeSlotZoneStartPaint = ({ color, name }: { color: string; name: string }) => {
            new_time_slot_zone_state.name = name

            map_paint.onStartPaint({
                map: map_instance as ymaps.Map,
                color,
                callback: onTimeSlotZoneAreaPainted,
            })
        }

        const onNewSector = () => {
            new_sector_state.is_create = true
            new_sector_state.is_opened = true
        }

        const onNewDispatcherSector = () => {
            new_dispatcher_sector_state.is_create = true
            new_dispatcher_sector_state.is_opened = true
        }

        const onNewExpressZone = () => {
            new_express_zone_state.is_create = true
            new_express_zone_state.is_opened = true
        }

        const onNewTimeSlotZone = () => {
            new_time_slot_zone_state.is_create = true
            new_time_slot_zone_state.is_opened = true
        }

        const setMapCenterOnSector = (sector: Sector | DispatcherSector | TimeSlotZone) => {
            if ('city' in sector && sector.city) {
                if (sector.city.latitude && sector.city.longitude) {
                    setMapCenter(map_instance as ymaps.Map, [
                        sector.city.latitude,
                        sector.city.longitude,
                    ])
                    return
                }
            }

            if (typeof sector.coordinates === 'string') {
                const centroid = map_graphics.getPolygonCentroid(JSON.parse(sector.coordinates))
                setMapCenter(map_instance as ymaps.Map, centroid)
            } else {
                const centroid = map_graphics.getPolygonCentroid(sector.coordinates)
                setMapCenter(map_instance as ymaps.Map, centroid)
            }
        }

        const onUpdateSector = (sector: Sector, sector_index: number) => {
            new_sector_state.is_create = false
            new_sector_state.is_update = true
            new_sector_state.initial_value = { ...sector }
            new_sector_state.sector_index = sector_index
            new_sector_state.is_opened = true

            setMapCenterOnSector(sector)
            map_graphics.startUpdatePolygonByIndex(map_instance as ymaps.Map, sector_index)
        }

        const onUpdateDispatcherSector = (sector: DispatcherSector, sector_index: number) => {
            new_dispatcher_sector_state.is_create = false
            new_dispatcher_sector_state.is_update = true
            new_dispatcher_sector_state.initial_value = { ...sector }
            new_dispatcher_sector_state.sector_index = sector_index
            new_dispatcher_sector_state.is_opened = true

            setMapCenterOnSector(sector)
            map_graphics.startUpdatePolygonByIndex(map_instance as ymaps.Map, sector_index)
        }

        const onUpdateExpressZone = (expressZone: ExpressZone, express_zone_index: number) => {
            new_express_zone_state.is_create = false
            new_express_zone_state.is_update = true
            new_express_zone_state.initial_value = { ...expressZone }
            new_express_zone_state.express_zone_index = express_zone_index
            new_express_zone_state.is_opened = true

            setMapCenterOnSector(expressZone)
            map_graphics.startUpdatePolygonByIndex(map_instance as ymaps.Map, express_zone_index)
        }

        const onUpdateTimeSlotZone = (timeSlotZone: TimeSlotZone, time_slot_zone_index: number) => {
            new_time_slot_zone_state.is_create = false
            new_time_slot_zone_state.is_update = true
            new_time_slot_zone_state.initial_value = { ...timeSlotZone }
            new_time_slot_zone_state.time_slot_zone_index = time_slot_zone_index
            new_time_slot_zone_state.is_opened = true

            setMapCenterOnSector(timeSlotZone)
            map_graphics.startUpdatePolygonByIndex(map_instance as ymaps.Map, time_slot_zone_index)
        }

        const onSectorFormCancel = () => {
            if (new_sector_state.coordinates.length) {
                removeLastAddedGeoObject(map_instance as ymaps.Map)
            }

            if (new_sector_state.is_update) {
                map_graphics.stopUpdatePolygonByIndex(
                    map_instance as ymaps.Map,
                    new_sector_state.sector_index as number
                )

                clearMap(map_instance as ymaps.Map)
                drawSectors(list_state.options)
            }

            closeNewSectorState()
        }

        const onDispatcherSectorFormCancel = () => {
            if (new_dispatcher_sector_state.coordinates.length) {
                removeLastAddedGeoObject(map_instance as ymaps.Map)
            }

            if (new_dispatcher_sector_state.is_update) {
                map_graphics.stopUpdatePolygonByIndex(
                    map_instance as ymaps.Map,
                    new_dispatcher_sector_state.sector_index as number
                )

                clearMap(map_instance as ymaps.Map)
                drawSectors(dispatcher_list_state.options)
            }

            closeNewDispatcherSectorState()
        }

        const onExpressZoneFormCancel = () => {
            if (new_express_zone_state.coordinates.length) {
                removeLastAddedGeoObject(map_instance as ymaps.Map)
            }

            if (new_express_zone_state.is_update) {
                map_graphics.stopUpdatePolygonByIndex(
                    map_instance as ymaps.Map,
                    new_express_zone_state.express_zone_index as number
                )

                clearMap(map_instance as ymaps.Map)
                drawSectors(express_zones_state.options)
            }

            closeNewExpressZoneState()
        }

        const onTimeSlotZoneFormCancel = () => {
            if (new_time_slot_zone_state.coordinates.length) {
                removeLastAddedGeoObject(map_instance as ymaps.Map)
            }

            if (new_time_slot_zone_state.is_update) {
                map_graphics.stopUpdatePolygonByIndex(
                    map_instance as ymaps.Map,
                    new_time_slot_zone_state.time_slot_zone_index as number
                )

                clearMap(map_instance as ymaps.Map)
                drawSectors(time_slot_zones_state.options)
            }

            closeNewTimeSlotZoneState()
        }

        const createSector = async ({
            name,
            dispatcherSectorId,
            color,
        }: Omit<SectorPayload, 'coordinates'>) => {
            const new_polygon_index = getLastAddedGeoObjectIndex(map_instance as ymaps.Map)
            map_graphics.stopUpdatePolygonByIndex(map_instance as ymaps.Map, new_polygon_index)

            try {
                new_sector_state.is_loading = true

                // recommend to use this function for get actual coordinates of polygon
                const coordinates = map_graphics.getActualPolygonCoordinatesByIndex(
                    map_instance as ymaps.Map,
                    new_polygon_index
                )

                const payload: SectorPayload = {
                    name,
                    dispatcherSectorId,
                    color,
                    coordinates,
                }

                // reloading because this response is returning null after success
                await sectorsAPI.createSector(payload)
                onSectorsReload()

                // commented because update in create mode disabled
                // map_graphics.updatePolygonByIndex(map_instance as ymaps.Map, new_polygon_index, {
                //     color,
                //     name
                // })

                notifications.notify({ type: 'success', title: 'Сектор создан' })

                closeNewSectorState()
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При создании сектора произошла ошибка' })
            } finally {
                new_sector_state.is_loading = false
            }
        }

        const createDispatcherSector = async ({
            name,
            description,
            dispatcherIds,
            cityId,
        }: Omit<DispatcherSectorFormPayload, 'coordinates'>) => {
            const new_polygon_index = getLastAddedGeoObjectIndex(map_instance as ymaps.Map)
            map_graphics.stopUpdatePolygonByIndex(map_instance as ymaps.Map, new_polygon_index)

            try {
                new_dispatcher_sector_state.is_loading = true

                // recommend to use this function for get actual coordinates of polygon
                const coordinates = map_graphics.getActualPolygonCoordinatesByIndex(
                    map_instance as ymaps.Map,
                    new_polygon_index
                )

                const payload: DispatcherSectorFormPayload = {
                    name,
                    description,
                    dispatcherIds,
                    cityId,
                    coordinates,
                }

                // reloading because this response is returning null after success
                await dispatcherSectorsAPI.createDispatcherSector(payload)
                onSectorsReload()

                // commented because update in create mode disabled
                // map_graphics.updatePolygonByIndex(map_instance as ymaps.Map, new_polygon_index, {
                //     color,
                //     name
                // })

                notifications.notify({ type: 'success', title: 'Сектор диспетчера создан' })

                closeNewDispatcherSectorState()
                onSectorsReload()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При создании сектора диспетчера произошла ошибка',
                })
            } finally {
                new_dispatcher_sector_state.is_loading = false
            }
        }

        const createExpressZone = async ({
            name,
            dispatcherSectorId,
            color,
            typeId
        }: Omit<CreateExpressZonePayload, 'coordinates'>) => {
            const new_polygon_index = getLastAddedGeoObjectIndex(map_instance as ymaps.Map)
            map_graphics.stopUpdatePolygonByIndex(map_instance as ymaps.Map, new_polygon_index)

            try {
                new_express_zone_state.is_loading = true

                // recommend to use this function for get actual coordinates of polygon
                const coordinates = map_graphics.getActualPolygonCoordinatesByIndex(
                    map_instance as ymaps.Map,
                    new_polygon_index
                )

                const payload: CreateExpressZonePayload = {
                    name,
                    dispatcherSectorId,
                    color,
                    typeId,
                    coordinates,
                }

                // reloading because this response is returning null after success
                await dispatcherSectorsAPI.createExpressZone(payload)
                onSectorsReload()

                // commented because update in create mode disabled
                // map_graphics.updatePolygonByIndex(map_instance as ymaps.Map, new_polygon_index, {
                //     color,
                //     name
                // })

                notifications.notify({ type: 'success', title: 'Экспресс зона создана' })

                closeNewExpressZoneState()
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При создании экспресс зоны произошла ошибка' })
            } finally {
                new_express_zone_state.is_loading = false
            }
        }

        const createTimeSlotZone = async ({
            name,
            dispatcherSectorId,
            color,
        }: Omit<TimeSlotZonePayload, 'coordinates'>) => {
            const new_polygon_index = getLastAddedGeoObjectIndex(map_instance as ymaps.Map)
            map_graphics.stopUpdatePolygonByIndex(map_instance as ymaps.Map, new_polygon_index)

            try {
                new_time_slot_zone_state.is_loading = true

                const coordinates = map_graphics.getActualPolygonCoordinatesByIndex(
                    map_instance as ymaps.Map,
                    new_polygon_index
                )

                const payload: TimeSlotZonePayload = {
                    name,
                    dispatcherSectorId,
                    color,
                    coordinates,
                }

                await timeSlotZonesAPI.createTimeSlotZone(payload)
                onSectorsReload()

                notifications.notify({ type: 'success', title: 'Сектор тайм-слотов создан' })

                closeNewTimeSlotZoneState()
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При создании сектора тайм-слотов произошла ошибка' })
            } finally {
                new_time_slot_zone_state.is_loading = false
            }
        }

        const updateSector = async ({
            id,
            payload,
        }: {
            id: number
            payload: Omit<SectorPayload, 'coordinates'>
        }) => {
            try {
                map_graphics.stopUpdatePolygonByIndex(
                    map_instance as ymaps.Map,
                    new_sector_state.sector_index as number
                )

                new_sector_state.is_loading = true

                // recommend to use this function for get actual coordinates of polygon
                const coordinates = map_graphics.getActualPolygonCoordinatesByIndex(
                    map_instance as ymaps.Map,
                    new_sector_state.sector_index as number
                )

                const update_payload: SectorPayload = {
                    name: payload.name,
                    dispatcherSectorId: payload.dispatcherSectorId,
                    color: payload.color,
                    coordinates,
                }

                await sectorsAPI.updateSector(id, update_payload)

                map_graphics.updatePolygonByIndex(
                    map_instance as ymaps.Map,
                    new_sector_state.sector_index as number,
                    {
                        color: payload.color,
                        name: payload.name,
                    }
                )

                onSectorsReload()

                notifications.notify({ type: 'success', title: 'Сектор сохранен' })

                closeNewSectorState()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При сохранении сектора произошла ошибка',
                })
            } finally {
                new_sector_state.is_loading = false
            }
        }

        const updateExpressZone = async ({
            id,
            payload,
        }: {
            id: number
            payload: Omit<UpdateExpressZonePayload, 'coordinates'>
        }) => {
            try {
                map_graphics.stopUpdatePolygonByIndex(
                    map_instance as ymaps.Map,
                    new_express_zone_state.express_zone_index as number
                )

                new_express_zone_state.is_loading = true

                // recommend to use this function for get actual coordinates of polygon
                const coordinates = map_graphics.getActualPolygonCoordinatesByIndex(
                    map_instance as ymaps.Map,
                    new_express_zone_state.express_zone_index as number
                )

                const update_payload: UpdateExpressZonePayload = {
                    name: payload.name,
                    dispatcherSectorId: payload.dispatcherSectorId,
                    color: payload.color,
                    typeId: payload.typeId,
                    coordinates,
                }

                await dispatcherSectorsAPI.updateExpressZone(id, update_payload)

                map_graphics.updatePolygonByIndex(
                    map_instance as ymaps.Map,
                    new_express_zone_state.express_zone_index as number,
                    {
                        color: payload.color,
                        name: payload.name,
                    }
                )

                onSectorsReload()

                notifications.notify({ type: 'success', title: 'Экспресс зона сохранена' })

                closeNewExpressZoneState()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При сохранении экспресс зоны произошла ошибка',
                })
            } finally {
                new_express_zone_state.is_loading = false
            }
        }

        const updateTimeSlotZone = async ({
            id,
            payload,
        }: {
            id: number
            payload: Omit<TimeSlotZonePayload, 'coordinates'>
        }) => {
            try {
                map_graphics.stopUpdatePolygonByIndex(
                    map_instance as ymaps.Map,
                    new_time_slot_zone_state.time_slot_zone_index as number
                )

                new_time_slot_zone_state.is_loading = true

                const coordinates = map_graphics.getActualPolygonCoordinatesByIndex(
                    map_instance as ymaps.Map,
                    new_time_slot_zone_state.time_slot_zone_index as number
                )

                const update_payload: TimeSlotZonePayload = {
                    name: payload.name,
                    dispatcherSectorId: payload.dispatcherSectorId,
                    color: payload.color,
                    coordinates,
                }

                await timeSlotZonesAPI.updateTimeSlotZone(id, update_payload)

                map_graphics.updatePolygonByIndex(
                    map_instance as ymaps.Map,
                    new_time_slot_zone_state.time_slot_zone_index as number,
                    {
                        color: payload.color,
                        name: payload.name,
                    }
                )

                onSectorsReload()

                notifications.notify({ type: 'success', title: 'Сектор тайм-слотов сохранен' })

                closeNewTimeSlotZoneState()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При сохранении сектора тайм-слотов произошла ошибка',
                })
            } finally {
                new_time_slot_zone_state.is_loading = false
            }
        }

        const updateDispatcherSector = async ({
            id,
            payload,
        }: {
            id: number
            payload: Omit<DispatcherSectorFormPayload, 'coordinates'>
        }) => {
            try {
                map_graphics.stopUpdatePolygonByIndex(
                    map_instance as ymaps.Map,
                    new_dispatcher_sector_state.sector_index as number
                )

                new_dispatcher_sector_state.is_loading = true

                // recommend to use this function for get actual coordinates of polygon
                const coordinates = map_graphics.getActualPolygonCoordinatesByIndex(
                    map_instance as ymaps.Map,
                    new_dispatcher_sector_state.sector_index as number
                )

                const update_payload: DispatcherSectorFormPayload = {
                    name: payload.name,
                    description: payload.description,
                    cityId: payload.cityId,
                    dispatcherIds: payload.dispatcherIds,
                    coordinates,
                }

                await dispatcherSectorsAPI.updateDispatcherSector(id, update_payload)

                map_graphics.updatePolygonByIndex(
                    map_instance as ymaps.Map,
                    new_dispatcher_sector_state.sector_index as number,
                    {
                        name: payload.description,
                    }
                )

                onSectorsReload()

                notifications.notify({ type: 'success', title: 'Сектор диспетчера сохранен' })

                closeNewDispatcherSectorState()
                onSectorsReload()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При сохранении сектора диспетчера произошла ошибка',
                })
            } finally {
                new_dispatcher_sector_state.is_loading = false
            }
        }

        const scrollWindowToMap = () => {
            const element = document.getElementById('map_layer')
            if (!element) return

            element.scrollIntoView({ behavior: 'smooth' })
        }

        const onSectorView = (sector: Sector | DispatcherSector | ExpressZone | TimeSlotZone) => {
            scrollWindowToMap()
            setMapCenterOnSector(sector)
        }

        const onExtendedSectorPainted = ({ coordinates, dispatcherSector }: { coordinates: number[][]; dispatcherSector: DispatcherSector }) => {
            new_extended_sector_state.coordinates = coordinates

            const polygon = map_graphics.createPolygon({
                id: `extended-${dispatcherSector.id}`,
                coordinates,
                color: '#FF0000',
                hintContent: `Внешний сектор ${dispatcherSector.description}`,
            })

            addGeoObject(map_instance as ymaps.Map, polygon)

            const new_polygon_index = getLastAddedGeoObjectIndex(map_instance as ymaps.Map)
            map_graphics.startUpdatePolygonByIndex(map_instance as ymaps.Map, new_polygon_index)
        }

        const onCreateExtendedSector = (dispatcherSector: DispatcherSector) => {
            new_extended_sector_state.dispatcherSector = dispatcherSector
            new_extended_sector_state.is_opened = true
            new_extended_sector_state.is_create = true
            new_extended_sector_state.is_update = false

            scrollWindowToMap()

            map_paint.onStartPaint({
                map: map_instance as ymaps.Map,
                color: '#2B55FF',
                callback: ({ coordinates }) => onExtendedSectorPainted({ coordinates, dispatcherSector }),
            })
        }

        const onUpdateExtendedSector = (dispatcherSector: DispatcherSector, dispatcherSectorIndex: number) => {
            if (new_extended_sector_state.sector_id) {
                map_graphics.stopUpdatePolygonById(map_instance as ymaps.Map, new_extended_sector_state.sector_id)
            }

            new_extended_sector_state.dispatcherSector = dispatcherSector
            new_extended_sector_state.is_opened = true
            new_extended_sector_state.is_create = false
            new_extended_sector_state.is_update = true

            setMapCenterOnSector(dispatcherSector)
            scrollWindowToMap()

            new_extended_sector_state.sector_id = `extended-${dispatcherSector.id}`
            map_graphics.startUpdatePolygonById(map_instance as ymaps.Map, new_extended_sector_state.sector_id as string)
        }

        const closeExtendedSectorState = () => {
            new_extended_sector_state.is_opened = false
            new_extended_sector_state.is_create = false
            new_extended_sector_state.is_update = false
            new_extended_sector_state.dispatcherSector = null
            new_extended_sector_state.sector_id = null
            new_extended_sector_state.coordinates = []
        }

        const onExtendedSectorFormCancel = () => {
            if (new_extended_sector_state.coordinates.length) {
                removeLastAddedGeoObject(map_instance as ymaps.Map)
            }

            if (new_extended_sector_state.is_update) {
                map_graphics.stopUpdatePolygonById(
                    map_instance as ymaps.Map,
                    new_extended_sector_state.sector_id as string
                )

                clearMap(map_instance as ymaps.Map)
                drawSectors(dispatcher_list_state.options)
            }

            closeExtendedSectorState()
        }

        const createExtendedSector = async ({ dispatcherSector }: { dispatcherSector: DispatcherSector }) => {
            const new_polygon_index = getLastAddedGeoObjectIndex(map_instance as ymaps.Map)
            map_graphics.stopUpdatePolygonByIndex(map_instance as ymaps.Map, new_polygon_index)

            try {
                new_extended_sector_state.is_loading = true

                const coordinates = map_graphics.getActualPolygonCoordinatesByIndex(
                    map_instance as ymaps.Map,
                    new_polygon_index
                )

                await dispatcherSectorsAPI.setExtendedCoordinate(dispatcherSector.id, coordinates)
                notifications.notify({ type: 'success', title: 'Внешний сектор добавлен' })

                closeExtendedSectorState()
                onSectorsReload()
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При добавлении внешнего сектора произошла ошибка' })
            } finally {
                new_extended_sector_state.is_loading = false
            }
        }

        const updateExtendedSector = async ({ dispatcherSector }: { dispatcherSector: DispatcherSector }) => {
            map_graphics.stopUpdatePolygonById(map_instance as ymaps.Map, new_extended_sector_state.sector_id as string)

            try {
                new_extended_sector_state.is_loading = true

                const coordinates = map_graphics.getActualPolygonCoordinatesById(
                    map_instance as ymaps.Map,
                    new_extended_sector_state.sector_id as string
                )

                await dispatcherSectorsAPI.setExtendedCoordinate(dispatcherSector.id, coordinates as number[][])
                notifications.notify({ type: 'success', title: 'Внешний сектор сохранен' })

                closeExtendedSectorState()
                onSectorsReload()
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При сохранении внешнего сектора произошла ошибка' })
            } finally {
                new_extended_sector_state.is_loading = false
            }
        }

        // New Dispatcher Sector Listener
        watch(
            () => new_dispatcher_store.dispatcherSectorIds,
            () => {
                reloadData()
            }
        )

        const isNewSectorAdded = computed(() => {
            if (new_sector_state.coordinates.length) {
                return true
            }

            return false
        })

        const isNewDispatcherSectorAdded = computed(() => {
            if (new_dispatcher_sector_state.coordinates.length) {
                return true
            }

            return false
        })

        const isNewExtendedSectorAdded = computed(() => {
            if (new_extended_sector_state.coordinates.length) {
                return true
            }

            return false
        })

        const isNewExpressZoneAdded = computed(() => {
            if (new_express_zone_state.coordinates.length) {
                return true
            }

            return false
        })

        const isNewTimeSlotZoneAdded = computed(() => {
            if (new_time_slot_zone_state.coordinates.length) {
                return true
            }

            return false
        })

        const isCanSectorCreate = computed(() => {
            if (havePermissions(['dispatcher.dispatcher-sectors-store', 'all-root-permission'])) {
                return true
            }

            return false
        })

        const isCanDispatcherSectorCreate = computed(() => {
            if (havePermissions(['dispatcher.sector-store', 'all-root-permission'])) {
                return true
            }

            return false
        })

        const isCanExpressZoneCreate = computed(() => {
            if (havePermissions(['dispatcher.express-zone-store', 'all-root-permission'])) {
                return true
            }

            return false
        })

        const isCanTimeSlotZoneCreate = computed(() => {
            if (havePermissions(['dispatcher.dispatcher-sectors-store', 'all-root-permission'])) {
                return true
            }

            return false
        })

        const isCanSectorUpdate = computed(() => {
            if (havePermissions(['dispatcher.sector-update', 'all-root-permission'])) {
                return true
            }

            return false
        })

        const isCanDispatcherSectorUpdate = computed(() => {
            if (havePermissions(['dispatcher.dispatcher-sectors-update', 'all-root-permission'])) {
                return true
            }

            return false
        })

        const isCanExpressZoneUpdate = computed(() => {
            if (havePermissions(['dispatcher.express-zone-update', 'all-root-permission'])) {
                return true
            }

            return false
        })

        const isCanTimeSlotZoneUpdate = computed(() => {
            if (havePermissions(['dispatcher.sector-update', 'all-root-permission'])) {
                return true
            }

            return false
        })

        const isCanSectorDelete = computed(() => {
            if (havePermissions(['dispatcher.sector-delete', 'all-root-permission'])) {
                return true
            }

            return false
        })

        const isCanDispatcherSectorDelete = computed(() => {
            if (havePermissions(['dispatcher.dispatcher-sectors-delete', 'all-root-permission'])) {
                return true
            }

            return false
        })

        const isCanExpressZoneDelete = computed(() => {
            if (havePermissions(['dispatcher.express-zone-delete', 'all-root-permission'])) {
                return true
            }

            return false
        })

        const isCanTimeSlotZoneDelete = computed(() => {
            if (havePermissions(['dispatcher.sector-delete', 'all-root-permission'])) {
                return true
            }

            return false
        })

        return {
            state,
            map_paint,
            list_state,
            dispatcher_list_state,
            express_zones_state,
            time_slot_zones_state,
            delete_state,
            new_sector_state,
            new_dispatcher_sector_state,
            new_extended_sector_state,
            new_express_zone_state,
            new_time_slot_zone_state,
            couriers_state,
            //
            onSectorsReload,
            onDeleteSector,
            onDeleteExpressZone,
            onDeleteTimeSlotZone,
            onCancelDeleteSector,
            deleteHandler,
            onMapReady,
            onStartPaint,
            onDispatcherStartPaint,
            onExpressZoneStartPaint,
            onTimeSlotZoneStartPaint,
            onSectorFormCancel,
            onDispatcherSectorFormCancel,
            onExpressZoneFormCancel,
            onTimeSlotZoneFormCancel,
            onNewSector,
            onNewExpressZone,
            onNewTimeSlotZone,
            onNewDispatcherSector,
            onUpdateSector,
            onUpdateDispatcherSector,
            onUpdateExpressZone,
            onUpdateTimeSlotZone,
            createSector,
            createDispatcherSector,
            createExpressZone,
            createTimeSlotZone,
            updateSector,
            updateDispatcherSector,
            updateExpressZone,
            updateTimeSlotZone,
            onSectorView,
            onExtendedSectorsChange,
            onCreateExtendedSector,
            onUpdateExtendedSector,
            onExtendedSectorFormCancel,
            createExtendedSector,
            updateExtendedSector,
            loadCouriers,
            //
            MAP_LAYERS,
            SECTORS_OPTIONS,
            //
            isNewSectorAdded,
            isNewDispatcherSectorAdded,
            isNewExtendedSectorAdded,
            isNewExpressZoneAdded,
            isNewTimeSlotZoneAdded,
            //
            isCanSectorCreate,
            isCanDispatcherSectorCreate,
            isCanExpressZoneCreate,
            isCanTimeSlotZoneCreate,
            isCanSectorUpdate,
            isCanDispatcherSectorUpdate,
            isCanExpressZoneUpdate,
            isCanTimeSlotZoneUpdate,
            isCanSectorDelete,
            isCanDispatcherSectorDelete,
            isCanExpressZoneDelete,
            isCanTimeSlotZoneDelete,
        }
    },
})
